import type { EntityFormState } from "@/app/EntityDrawer";
import { TextField } from "@/app/fields";
import type { Channel, ChannelGroup } from "@/app/master";
import { nameOf } from "@/app/queries";
import type { RoutingRule } from "@/app/routing";
import { Alert, Button, Combobox, Field, Grid, Switch, Textarea } from "@/ui";
import { conditionSummary } from "./routeConditionDraft";
import { routingRuleTargetName } from "./routingRuleDraft";
import { newTemplateItem, type RoutingTemplateDraft, type TemplateItemDraft } from "./routingTemplateDraft";

/** 模板成员编辑只改绑定，不修改共享规则的条件或目标（docs/10 §10.13）。 */
export function RoutingTemplateForm({ draft, errors, set, rules, channels, groups, disabled, isNew }: EntityFormState<RoutingTemplateDraft> & {
  rules: RoutingRule[]; channels: Channel[]; groups: ChannelGroup[]; disabled: boolean; isNew: boolean;
}) {
  const names = { channel: (id: number) => nameOf(channels, id), group: (id: number) => nameOf(groups, id) };
  const update = (key: number, patch: Partial<TemplateItemDraft>) => set("items", draft.items.map(b => b.key === key ? { ...b, ...patch } : b));
  const options = rules.map(r => ({ value: String(r.id), label: r.name, hint: `#${r.id}`, desc: `${conditionSummary(r.match)} → ${routingRuleTargetName(r, names)}` }));
  return <Grid>
    <TextField id="template-name" label="模板名称" required value={draft.name} error={errors.name} onChange={v => set("name", v)} />
    <Field label="备注" htmlFor="template-note"><Textarea id="template-note" rows={2} value={draft.note} onChange={e => set("note", e.target.value)} /></Field>
    {!isNew && <Switch label="启用模板" checked={draft.status === "active"} onChange={on => set("status", on ? "active" : "disabled")} />}
    <Alert tone="neutral" title="模板内小数字先匹配">优先级只在本模板比较，不能重复；无条件兜底只能最后。模板停用或无匹配时继续账号的下一条绑定；命中规则但目标不可用时返回无路由。保存后立即影响后续新首发。</Alert>
    <Field label="模板规则绑定" error={errors.items} hint="选择已有独立规则；这里仅调整绑定，规则条件与目标仍由所有引用者共享。">
      <Grid>{draft.items.map((b, i) => {
        const rule = rules.find(r => String(r.id) === b.rule_id);
        const missing = b.rule_id && !options.some(o => o.value === b.rule_id);
        return <div key={b.key} className="ax-fieldset" role="group" aria-label={`第 ${i + 1} 条模板绑定`}>
          <Grid>
            <Field label="规则"><Combobox searchable block disabled={disabled} ariaLabel={`第 ${i + 1} 条模板规则`} value={b.rule_id || null}
              options={missing ? [...options, { value: b.rule_id, label: `规则 #${b.rule_id} 不可绑定`, hint: "不可用", desc: "规则不存在", disabled: true }] : options} onChange={rule_id => update(b.key, { rule_id })} /></Field>
            {rule && <p className="ax-secondary">{conditionSummary(rule.match)} → {routingRuleTargetName(rule, names)}；规则版本 {rule.version}</p>}
            <Grid columns="1fr auto">
              <TextField id={`template-priority-${b.key}`} label={`第 ${i + 1} 条优先级`} mono value={b.priority} onChange={priority => update(b.key, { priority })} />
              <div className="ax-row"><Button disabled={disabled} size="sm" variant="ghost" onClick={() => set("items", draft.items.filter(it => it.key !== b.key))}>移除第 {i + 1} 条</Button></div>
            </Grid>
          </Grid>
        </div>;
      })}</Grid>
    </Field>
    <Button disabled={disabled} onClick={() => set("items", [...draft.items, newTemplateItem()])}>添加规则绑定</Button>
  </Grid>;
}
